import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faLightbulb, 
  faFireFlameCurved, 
  faWifi, 
  faBuilding,
  faCheck,
  faPhone
} from '@fortawesome/free-solid-svg-icons';
import { motion, AnimatePresence } from 'framer-motion';
import CallToAction from '../components/CallToAction';
import { offerte, serviziBusiness } from '../utils/data';

const Offerte: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'luce' | 'gas' | 'internet' | 'business'>('luce');

  const handleTabChange = (tab: 'luce' | 'gas' | 'internet' | 'business') => {
    setActiveTab(tab);
  };

  // Funzione per ottenere l'icona corrispondente
  const getIconComponent = (iconName: string) => {
    switch (iconName) {
      case 'faLightbulb':
        return faLightbulb;
      case 'faFireFlameCurved':
        return faFireFlameCurved;
      case 'faWifi':
        return faWifi;
      default:
        return faLightbulb;
    }
  };

  return (
    <div>
      {/* Hero Section */}
      <section className="pt-32 pb-20 bg-gradient-to-br from-primary to-primary/80 text-white">
        <div className="container mx-auto">
          <motion.div 
            initial={{ opacity: 0, y: -30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-3xl mx-auto text-center"
          >
            <h1 className="text-4xl md:text-5xl font-bold mb-6">Confronta le Offerte</h1>
            <p className="text-xl text-white/80">
              Confronta le offerte luce, gas e internet disponibili sul mercato.
              Consulenza gratuita e senza obbligo di sottoscrizione.
            </p>
            <p className="text-sm text-white/50 mt-4">
              Le offerte mostrate hanno carattere indicativo e possono variare. Servizio indipendente gestito da GT Energie Srl.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Tabs Section */}
      <section className="py-20">
        <div className="container mx-auto">
          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="flex flex-wrap justify-center mb-12 border-b border-light-border"
          >
            <motion.button 
              whileHover={{ y: -3 }}
              whileTap={{ y: 0 }}
              className={`px-6 py-4 text-lg font-medium transition-all duration-300 border-b-2 mx-2 ${
                activeTab === 'luce' 
                  ? 'border-primary text-primary' 
                  : 'border-transparent text-light-muted hover:text-primary'
              }`}
              onClick={() => handleTabChange('luce')}
            >
              <FontAwesomeIcon icon={faLightbulb} className="mr-2" /> Offerte Luce
            </motion.button>
            <motion.button 
              whileHover={{ y: -3 }}
              whileTap={{ y: 0 }}
              className={`px-6 py-4 text-lg font-medium transition-all duration-300 border-b-2 mx-2 ${
                activeTab === 'gas' 
                  ? 'border-primary text-primary' 
                  : 'border-transparent text-light-muted hover:text-primary'
              }`}
              onClick={() => handleTabChange('gas')}
            >
              <FontAwesomeIcon icon={faFireFlameCurved} className="mr-2" /> Offerte Gas
            </motion.button>
            <motion.button 
              whileHover={{ y: -3 }}
              whileTap={{ y: 0 }}
              className={`px-6 py-4 text-lg font-medium transition-all duration-300 border-b-2 mx-2 ${
                activeTab === 'internet' 
                  ? 'border-primary text-primary' 
                  : 'border-transparent text-light-muted hover:text-primary'
              }`}
              onClick={() => handleTabChange('internet')}
            >
              <FontAwesomeIcon icon={faWifi} className="mr-2" /> Offerte Internet
            </motion.button>
            <motion.button 
              whileHover={{ y: -3 }}
              whileTap={{ y: 0 }}
              className={`px-6 py-4 text-lg font-medium transition-all duration-300 border-b-2 mx-2 ${
                activeTab === 'business' 
                  ? 'border-primary text-primary' 
                  : 'border-transparent text-light-muted hover:text-primary'
              }`}
              onClick={() => handleTabChange('business')}
            >
              <FontAwesomeIcon icon={faBuilding} className="mr-2" /> Soluzioni Business
            </motion.button>
          </motion.div>

          <AnimatePresence mode="wait">
            {/* Luce Content */}
            {activeTab === 'luce' && (
              <motion.div 
                key="luce"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.5 }}
              >
                <motion.div 
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5 }}
                  className="text-center mb-12"
                >
                  <h2 className="text-3xl font-bold mb-4 text-primary">Offerte Luce</h2>
                  <p className="text-lg text-light-muted max-w-3xl mx-auto">
                    Scegli l'offerta più adatta alle tue esigenze. Tariffe chiare e trasparenti per la tua casa o attività.
                  </p>
                </motion.div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                  {offerte.luce.map((offerta, index) => (
                    <motion.div 
                      key={offerta.id}
                      initial={{ opacity: 0, y: 30 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.5, delay: index * 0.1 }}
                      whileHover={{ y: -10, transition: { duration: 0.2 } }}
                      className={`card ${offerta.popolare ? 'border-2 border-primary shadow-lg relative' : 'border border-light-border hover:border-primary transition-all duration-300'}`}
                    >
                      {offerta.popolare && (
                        <div className="absolute top-0 right-0 bg-primary text-white py-1 px-4 text-sm font-medium rounded-bl-lg">
                          Più Popolare
                        </div>
                      )}
                      <div className="text-center p-8">
                        <h3 className="text-2xl font-bold mb-2 text-primary">{offerta.titolo}</h3>
                        <p className="text-light-muted mb-6">{offerta.descrizione}</p>
                        <div className="text-4xl font-bold text-primary mb-6">
                          {offerta.prezzo.toFixed(2).replace('.', ',')}€<span className="text-lg font-normal text-light-muted">{offerta.unitaMisura}</span>
                        </div>

                        <ul className="space-y-3 text-left mb-8">
                          {offerta.caratteristiche.map((caratteristica, index) => (
                            <li key={index} className="flex items-center">
                              <FontAwesomeIcon icon={faCheck} className="text-green-500 mr-2" />
                              <span className="text-light-text">{caratteristica}</span>
                            </li>
                          ))}
                        </ul>

                        <motion.a 
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                          href="tel:+390240055045" 
                          className="btn btn-primary w-full"
                        >
                          <FontAwesomeIcon icon={faPhone} className="mr-2" /> Richiedi Informazioni
                        </motion.a>
                        <p className="text-xs text-light-muted mt-2">Prezzo indicativo soggetto a variazioni</p>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </motion.div>
            )}

            {/* Gas Content */}
            {activeTab === 'gas' && (
              <motion.div 
                key="gas"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.5 }}
              >
                <motion.div 
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5 }}
                  className="text-center mb-12"
                >
                  <h2 className="text-3xl font-bold mb-4 text-primary">Offerte Gas</h2>
                  <p className="text-lg text-light-muted max-w-3xl mx-auto">
                    Riscalda la tua casa con le nostre offerte gas convenienti e trasparenti.
                  </p>
                </motion.div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                  {offerte.gas.map((offerta, index) => (
                    <motion.div 
                      key={offerta.id}
                      initial={{ opacity: 0, y: 30 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.5, delay: index * 0.1 }}
                      whileHover={{ y: -10, transition: { duration: 0.2 } }}
                      className={`card ${offerta.popolare ? 'border-2 border-primary shadow-lg relative' : 'border border-light-border hover:border-primary transition-all duration-300'}`}
                    >
                      {offerta.popolare && (
                        <div className="absolute top-0 right-0 bg-primary text-white py-1 px-4 text-sm font-medium rounded-bl-lg">
                          Più Popolare
                        </div>
                      )}
                      <div className="text-center p-8">
                        <h3 className="text-2xl font-bold mb-2 text-primary">{offerta.titolo}</h3>
                        <p className="text-light-muted mb-6">{offerta.descrizione}</p>
                        <div className="text-4xl font-bold text-primary mb-6">
                          {offerta.prezzo.toFixed(2).replace('.', ',')}€<span className="text-lg font-normal text-light-muted">{offerta.unitaMisura}</span>
                        </div>

                        <ul className="space-y-3 text-left mb-8">
                          {offerta.caratteristiche.map((caratteristica, index) => (
                            <li key={index} className="flex items-center">
                              <FontAwesomeIcon icon={faCheck} className="text-green-500 mr-2" />
                              <span className="text-light-text">{caratteristica}</span>
                            </li>
                          ))}
                        </ul>

                        <motion.a 
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                          href="tel:+390240055045" 
                          className="btn btn-primary w-full"
                        >
                          <FontAwesomeIcon icon={faPhone} className="mr-2" /> Richiedi Informazioni
                        </motion.a>
                        <p className="text-xs text-light-muted mt-2">Prezzo indicativo soggetto a variazioni</p>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </motion.div>
            )}

            {/* Internet Content */}
            {activeTab === 'internet' && (
              <motion.div 
                key="internet"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.5 }}
              >
                <motion.div 
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5 }}
                  className="text-center mb-12"
                >
                  <h2 className="text-3xl font-bold mb-4 text-primary">Offerte Internet</h2>
                  <p className="text-lg text-light-muted max-w-3xl mx-auto">
                    Connettiti al mondo con le nostre offerte internet veloci e affidabili.
                  </p>
                </motion.div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                  {offerte.internet.map((offerta, index) => (
                    <motion.div 
                      key={offerta.id}
                      initial={{ opacity: 0, y: 30 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.5, delay: index * 0.1 }}
                      whileHover={{ y: -10, transition: { duration: 0.2 } }}
                      className={`card ${offerta.popolare ? 'border-2 border-primary shadow-lg relative' : 'border border-light-border hover:border-primary transition-all duration-300'}`}
                    >
                      {offerta.popolare && (
                        <div className="absolute top-0 right-0 bg-primary text-white py-1 px-4 text-sm font-medium rounded-bl-lg">
                          Più Popolare
                        </div>
                      )}
                      <div className="text-center p-8">
                        <h3 className="text-2xl font-bold mb-2 text-primary">{offerta.titolo}</h3>
                        <p className="text-light-muted mb-6">{offerta.descrizione}</p>
                        <div className="text-4xl font-bold text-primary mb-6">
                          {offerta.prezzo.toFixed(2).replace('.', ',')}€<span className="text-lg font-normal text-light-muted">{offerta.unitaMisura}</span>
                        </div>

                        <ul className="space-y-3 text-left mb-8">
                          {offerta.caratteristiche.map((caratteristica, index) => (
                            <li key={index} className="flex items-center">
                              <FontAwesomeIcon icon={faCheck} className="text-green-500 mr-2" />
                              <span className="text-light-text">{caratteristica}</span>
                            </li>
                          ))}
                        </ul>

                        <motion.a 
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                          href="tel:+390240055045" 
                          className="btn btn-primary w-full"
                        >
                          <FontAwesomeIcon icon={faPhone} className="mr-2" /> Richiedi Informazioni
                        </motion.a>
                        <p className="text-xs text-light-muted mt-2">Prezzo indicativo soggetto a variazioni</p>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </motion.div>
            )}

            {/* Business Content */}
            {activeTab === 'business' && (
              <motion.div 
                key="business"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.5 }}
              >
                <motion.div 
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5 }}
                  className="text-center mb-12"
                >
                  <h2 className="text-3xl font-bold mb-4 text-primary">Soluzioni Business</h2>
                  <p className="text-lg text-light-muted max-w-3xl mx-auto">
                    Offerte personalizzate per la tua attività, con consulenza dedicata e assistenza prioritaria.
                  </p>
                </motion.div>

                <motion.div 
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5 }}
                  className="bg-light-surface p-8 rounded-lg shadow-md mb-12"
                >
                  <h3 className="text-2xl font-bold mb-4 text-primary">Soluzioni su Misura</h3>
                  <p className="mb-6 text-light-text">
                    Per le aziende offriamo pacchetti personalizzati che combinano energia elettrica, gas e connettività,
                    con tariffe studiate in base ai consumi specifici e alle esigenze della tua attività.
                  </p>
                  <p className="mb-6 text-light-text">
                    I nostri consulenti business sono a disposizione per un'analisi gratuita dei tuoi consumi
                    e per proporti la soluzione più vantaggiosa.
                  </p>
                  <div className="flex justify-center mt-8">
                    <motion.a 
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      href="tel:+390123456789" 
                      className="btn btn-primary"
                    >
                      <FontAwesomeIcon icon={faPhone} className="mr-2" /> Richiedi Consulenza
                    </motion.a>
                  </div>
                </motion.div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-12">
                  {serviziBusiness.map((servizio, index) => (
                    <motion.div 
                      key={servizio.id}
                      initial={{ opacity: 0, y: 30 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.5, delay: index * 0.1 }}
                      whileHover={{ y: -10, transition: { duration: 0.2 } }}
                      className="card text-center"
                    >
                      <div className="inline-block p-4 rounded-full bg-primary/10 text-primary mb-6">
                        <FontAwesomeIcon icon={getIconComponent(servizio.icona)} className="text-3xl" />
                      </div>
                      <h3 className="text-xl font-semibold mb-4 text-light-text">{servizio.titolo}</h3>
                      <p className="text-light-muted">{servizio.descrizione}</p>
                    </motion.div>
                  ))}
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Disclaimer Offerte */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="mt-12 p-6 bg-light-background rounded-lg border border-light-border/30 text-center"
          >
            <p className="text-xs text-light-muted leading-relaxed max-w-3xl mx-auto">
              Le offerte e i prezzi mostrati hanno carattere puramente indicativo e informativo. I prezzi e le condizioni possono variare 
              in qualsiasi momento su decisione dei fornitori. Per le condizioni contrattuali definitive, fare riferimento alla documentazione 
              ufficiale del fornitore prescelto. Servizio Bolletta è un servizio indipendente di comparazione gestito da GT Energie Srl — 
              Non siamo il servizio clienti ufficiale di alcun fornitore di energia.
            </p>
          </motion.div>

          {/* CTA Section */}
          <motion.div 
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="mt-12"
          >
            <CallToAction 
              title="Hai bisogno di aiuto per scegliere?" 
              subtitle="Parla con un nostro consulente per una consulenza gratuita e senza obbligo di sottoscrizione"
              variant="secondary"
            />
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Offerte; 